// The handful of theme colours needed where a Tailwind class can't reach:
// MapLibre paint properties, SVG strokes and alpha-blended shading. Everything
// styled through the DOM should use the Tailwind classes instead.
//
// Values mirror `src/index.css`; `theme.test.ts` enforces parity. (CRA's module scope
// forbids importing the Tailwind config from src/, so this is the JS-side table.)
export const theme = {
  brand500: "#4CAF50", // brand.500
  up: "#16A34A", // same as --ok (bg-ok dots and map fills); text uses text-ok-ink
  down: "#DC2626", // same as --bad (bg-bad dots and map fills); text uses text-bad-ink
  flat: "#9CA3AF",
  steady: "#F59E0B", // amber: change inside the steady band (traffic-light middle) // ink.faint
} as const;

/** `#RRGGBB` → [r, g, b] for deck.gl colour props. */
export function rgbOf(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `#RRGGBB` → `rgba(r,g,b,a)` for computed shading. */
export function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
}

/** Chart.js colours that must follow the page theme (canvas can't read CSS classes). */
export const chartColors = (dark: boolean) => ({
  grid: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
  tick: dark ? "#A1A7B0" : "#6B7280",
  tooltipBg: dark ? "rgba(26,29,34,0.95)" : "rgba(0,0,0,0.8)",
});

export type Rgb = readonly [number, number, number];

/** The tokens that canvas, Chart.js, MapLibre and deck.gl code needs, as in `src/index.css`. */
export const TOKEN_NAMES = [
  "brand-500", "brand-700", "surface", "on-brand", "ok", "warn", "bad", "note",
  "series-1", "series-2", "series-3", "series-4", "series-5",
  "series-6", "series-7", "series-8", "series-9", "series-10",
] as const;
export type TokenName = (typeof TOKEN_NAMES)[number];

export const tokenRgb: { light: Record<TokenName, Rgb>; dark: Record<TokenName, Rgb> } = {
  light: {
    "brand-500": [76, 175, 80],
    "brand-700": [56, 142, 60],
    surface: [255, 255, 255],
    "on-brand": [255, 255, 255],
    ok: [22, 163, 74],
    warn: [245, 158, 11],
    bad: [220, 38, 38],
    note: [59, 130, 246],
    "series-1": [76, 175, 80],
    "series-2": [55, 138, 221],
    "series-3": [216, 90, 48],
    "series-4": [127, 119, 221],
    "series-5": [186, 117, 23],
    "series-6": [212, 83, 126],
    "series-7": [99, 153, 34],
    "series-8": [160, 90, 216],
    "series-9": [23, 162, 160],
    "series-10": [120, 128, 140],
  },
  dark: {
    "brand-500": [76, 175, 80],
    "brand-700": [134, 205, 138],
    surface: [20, 22, 26],
    "on-brand": [255, 255, 255],
    ok: [22, 163, 74],
    warn: [245, 158, 11],
    bad: [220, 38, 38],
    note: [59, 130, 246],
    "series-1": [97, 185, 101],
    "series-2": [79, 152, 225],
    "series-3": [221, 110, 73],
    "series-4": [142, 135, 225],
    "series-5": [194, 134, 51],
    "series-6": [217, 104, 142],
    "series-7": [118, 165, 61],
    "series-8": [171, 110, 221],
    "series-9": [51, 173, 171],
    "series-10": [136, 143, 154],
  },
};

export interface ThemeColors {
  readonly dark: boolean;
  /** A fresh mutable copy, for deck.gl colour props. */
  rgb(name: TokenName): [number, number, number];
  /** `rgb(r,g,b)` or `rgba(r,g,b,0.500)`; the comma form works in MapLibre and Chart.js. Alpha is clamped to 0..1. */
  css(name: TokenName, alpha?: number): string;
  /** `css(series-1..10)`, in order. */
  readonly series: readonly string[];
  readonly chart: ReturnType<typeof chartColors>;
}

const SERIES_NAMES: readonly TokenName[] = [
  "series-1", "series-2", "series-3", "series-4", "series-5",
  "series-6", "series-7", "series-8", "series-9", "series-10",
];

function buildThemeColors(dark: boolean): ThemeColors {
  const table = dark ? tokenRgb.dark : tokenRgb.light;
  const css = (name: TokenName, alpha?: number): string => {
    const [r, g, b] = table[name];
    if (alpha === undefined) return `rgb(${r},${g},${b})`;
    return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
  };
  return {
    dark,
    rgb: (name) => [...table[name]] as [number, number, number],
    css,
    series: SERIES_NAMES.map((n) => css(n)),
    chart: chartColors(dark),
  };
}

const LIGHT_COLORS = buildThemeColors(false);
const DARK_COLORS = buildThemeColors(true);

/** The colours for one mode. Returns one of two module-level instances, so the reference is stable. */
export function themeColors(dark: boolean): ThemeColors {
  return dark ? DARK_COLORS : LIGHT_COLORS;
}

/** Carto basemaps: light (positron) and dark (dark-matter), same tile source. */
export const basemapStyle = (dark: boolean) =>
  dark
    ? "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
    : "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
